// src/features/locker-types/components/LockerTypeList.tsx
// Versión corregida con selector siempre visible

import React, { useMemo, useState, useEffect } from 'react';
import {
  List,
  TextField,
  FunctionField,
  useTranslate,
  ListProps,
  usePermissions,
  ReferenceInput,
  SelectInput,
  useGetIdentity,
  useGetList,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { 
  Category as CategoryIcon,
} from '@mui/icons-material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import CustomListToolbar from '../../../components/common/CustomListToolbar';
import LockerTypeActions from './LockerTypeActions';
import { useLockerTypePermissions } from '../hooks/useLockerTypePermissions';
import { LockerType } from '../types';
import { CustomTextInputFilter, CustomSelectInputFilter } from '@/components/common/CustomFilters';

// ============================================
// COMPONENTS
// ============================================

/**
 * Empty list component
 */
const EmptyLockerTypeList: React.FC = () => {
  const translate = useTranslate();
  
  return (
    <Paper sx={{ p: 4, m: 2, textAlign: 'center' }}>
      <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" paragraph>
        {translate('resources.locker-types.messages.no_types')}
      </Typography>
      <Typography color="text.secondary">
        {translate('resources.locker-types.messages.no_types_description')}
      </Typography>
    </Paper>
  );
};

/**
 * Type name field with icon
 */
const TypeNameField: React.FC<{ record?: LockerType }> = ({ record }) => {
  if (!record) return null;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {record.name}
      </Typography>
    </Box>
  );
};

/**
 * Description field
 */
const DescriptionField: React.FC<{ record?: LockerType }> = ({ record }) => {
  if (!record?.description) {
    return <Typography variant="caption" color="text.secondary">-</Typography>;
  }
  
  const truncated = record.description.length > 50 
    ? `${record.description.substring(0, 50)}...` 
    : record.description;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" title={record.description}>
        {truncated}
      </Typography>
    </Box>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const LockerTypeList: React.FC<ListProps> = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  const { canCreate, canManage } = useLockerTypePermissions();
  
  // Estado para el hotel seleccionado
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
  
  // Determinar roles y permisos
  const userRole = permissions || '';
  const isSuperAdmin = userRole === 'SuperAdmin';
  const isGroupAdmin = userRole === 'GroupAdmin';
  const isHotelAdmin = userRole === 'HotelAdmin';
  const isReceptionist = userRole === 'Receptionist';
  const canSelectHotel = isSuperAdmin || isGroupAdmin;
  
  // Determinar el filtro de hoteles según el rol
  const hotelFilter = useMemo(() => {
    if (isHotelAdmin || isReceptionist) {
      // Hotel fijo del usuario
      return identity?.hotelId ? { id: identity.hotelId } : {};
    } else if (isGroupAdmin) {
      // Solo hoteles del grupo
      return identity?.groupId ? { groupId: identity.groupId } : {};
    }
    // SuperAdmin ve todos
    return {};
  }, [isHotelAdmin, isReceptionist, isGroupAdmin, identity?.hotelId, identity?.groupId]);
  
  // Obtener lista de hoteles según el rol
  const { data: hotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: hotelFilter,
  }, {
    enabled: !identityLoading,
  });
  
  // Establecer hotel inicial
  useEffect(() => {
    if (!identityLoading && hotels && hotels.length > 0 && !selectedHotelId) {
      if ((isHotelAdmin || isReceptionist) && identity?.hotelId) {
        // HotelAdmin/Receptionist: usar su hotel fijo
        setSelectedHotelId(identity.hotelId);
      } else {
        // SuperAdmin/GroupAdmin: seleccionar el primer hotel
        setSelectedHotelId(hotels[0].id);
      }
    }
  }, [identityLoading, identity?.hotelId, hotels, selectedHotelId, isHotelAdmin, isReceptionist]);
  
  // Check if user can export
  const canExport = useMemo(() => {
    return ['SuperAdmin', 'GroupAdmin', 'HotelAdmin'].includes(userRole);
  }, [userRole]);
  
  // Define filters - SIEMPRE incluir el selector de hotel
  const filters = useMemo(() => [
   
    
    // Búsqueda
    <CustomTextInputFilter
      key="search"
      label={translate('resources.locker-types.filters.search')}
      source="q"
      alwaysOn
      sx={{ minWidth: 300 }}
    />,

     // SELECTOR DE HOTEL - Siempre visible, deshabilitado para roles de hotel único
    <ReferenceInput
      key="hotelId"
      source="hotelId"
      reference="hotels"
      label={translate('common.hotel')}
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
      filter={hotelFilter}
      perPage={100}
    >
      <CustomSelectInputFilter
        optionText="name"
        optionValue="id"
        disabled={isHotelAdmin || isReceptionist} // Deshabilitado para roles de hotel único
      />
    </ReferenceInput>,


  ], [translate, hotelFilter, isHotelAdmin, isReceptionist]);
  
  // Valores por defecto del filtro - siempre incluir hotelId
  const filterDefaultValues = useMemo(() => {
    if (selectedHotelId) {
      return { hotelId: selectedHotelId };
    }
    return {};
  }, [selectedHotelId]);
  
  // Check permissions
  if (!canManage) {
    return (
      <Paper sx={{ p: 3, m: 2 }}>
        <Typography color="error">
          {translate('resources.locker-types.messages.access_denied')}
        </Typography>
      </Paper>
    );
  }
  
  // Si todavía está cargando o no hay hotel seleccionado
  if (identityLoading || !selectedHotelId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Main list render
  return (
    <List
      {...props}
      title={translate('resources.locker-types.titles.list')}
      filters={filters}
      filterDefaultValues={filterDefaultValues}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      empty={<EmptyLockerTypeList />}
      // Key para forzar re-render cuando cambia el hotel
      key={`locker-types-${selectedHotelId}`}
      actions={
        <CustomListToolbar
          showFilterButton={false}
          showCreate={canCreate}
          createLabel={translate('resources.locker-types.actions.create')}
          showExport={canExport}
        />
      }
    >
      <CustomDataGrid
        rowClick="edit"
        bulkActionButtons={canCreate ? undefined : false}
      >
        {/* Actions column */}
        <FunctionField
          label=""
          render={(record: LockerType) => <LockerTypeActions record={record} />}
          sortable={false}
        />
        
        {/* Type name */}
        <FunctionField
          label={translate('resources.locker-types.fields.name')}
          render={(record: LockerType) => <TypeNameField record={record} />}
          sortBy="name"
        />
        
        {/* Description */}
        <FunctionField
          label={translate('resources.locker-types.fields.description')}
          render={(record: LockerType) => <DescriptionField record={record} />}
          sortable={false}
        />
        
        {/* Hotel name (solo visible para SuperAdmin/GroupAdmin) */}
        {canSelectHotel && (
          <TextField 
            source="hotel.name" 
            label={translate('resources.locker-types.fields.hotel')}
            sortBy="hotel.name"
          />
        )}
        
        {/* Created date */}
        <FunctionField
          label={translate('resources.locker-types.fields.createdAt')}
          render={(record: LockerType) => (
            <Typography variant="caption" color="text.secondary">
              {record.createdAt 
                ? new Date(record.createdAt).toLocaleDateString() 
                : '-'}
            </Typography>
          )}
          sortBy="createdAt"
        />
        
        {/* Updated date */}
        <FunctionField
          label={translate('resources.locker-types.fields.updatedAt')}
          render={(record: LockerType) => (
            <Typography variant="caption" color="text.secondary">
              {record.updatedAt 
                ? new Date(record.updatedAt).toLocaleDateString() 
                : '-'}
            </Typography>
          )}
          sortBy="updatedAt"
        />
      </CustomDataGrid>
    </List>
  );
};

export default LockerTypeList;